import { UIDataType, UISnowAttribute } from 'src/@types/serviceOnboardingTypes/attribute.types';
import {
	ApplicationProfileData,
	Status,
} from '@amzn/snow-data-service-typescript-client';
import { WizardStep } from 'src/components/commonComponents/GenericWizard/@types/GenericForm.types';
import { createWizardStep } from 'src/components/commonComponents/GenericWizard/utils/wizardUtils';
import { parseCommaSeparatedString } from 'src/utils/StringUtils';
import { Override } from 'src/@types/common';

export type ApplicationProfileFormMetadata = Override<
	ApplicationProfileData,
	{ subDomains: string }
>;

export const APPLICATION_FORM_ATTRIBUTES: UISnowAttribute[] = [
	{
		attributeId: 'applicationName',
		attributeName: 'Application Name',
		description:
			'Enter a unique identifier for your application that will be used across SNOW operations and integrations',
		dataType: UIDataType.STRING,
		mandatory: true,
		helpText:
			'Provide a descriptive name that clearly identifies your application (e.g., "UserAuthenticationApplication", "PaymentProcessingAPI")',
	},
	{
		attributeId: 'description',
		attributeName: 'Description',
		description:
			'Provide a detailed description of the application, explaining its purpose and impact in your organization or across Amazon',
		dataType: UIDataType.PARAGRAPH,
		mandatory: true,
		helpText:
			'Describe what this application does, its key functionality, and how it impacts Amazon\'s operation. Include operational considerations and business value.',
	},
	{
		attributeId: 'status',
		attributeName: 'Status',
		description:
			'Select the current operational status of the application to control its availability and usage',
		dataType: UIDataType.STRING,
		mandatory: true,
		options: [Status.ACTIVE, Status.PENDING, Status.DEPRECATED],
		value: Status.ACTIVE,
		helpText:
			'Active: Application is operational and available for use. Pending: Application is being set up or under review. Deprecated: Application is being phased out.',
	},
	{
		attributeId: 'domain',
		attributeName: 'Domain',
		description:
			'Select the high-level domain grouping that identifies the operational area of this application',
		dataType: UIDataType.LIST,
		mandatory: true,
		helpText:
			"Choose the domain that best categorizes your application's operational area. This helps with organization, access control, and operational workflows.",
		options: ['SNOW'],
		value: 'SNOW',
	},
	//TODO: Add this back in when domain + sub domain mappings are clearly defined.
	// {
	// 	attributeId: 'subDomains',
	// 	attributeName: 'Subdomains',
	// 	description:
	// 		'Specify lower-level groupings of the application under the selected domain for more granular categorization',
	// 	dataType: DataType.LIST,
	// 	mandatory: false,
	// 	helpText:
	// 		'Add relevant subdomains that provide more specific categorization within your chosen domain. This helps with detailed organization and operational routing.',
	// },
	{
		attributeId: 'bindleId',
		attributeName: 'Bindle ID',
		description:
			'Enter the Bindle resource identifier that is associated with the application.',
		dataType: UIDataType.STRING,
		mandatory: true,
		helpText:
			'Provide the complete Bindle ID starting with "amzn1.bindle.resource." followed by the resource identifier.',
		regex: '^amzn1\\.bindle\\.resource\\.[a-z0-9]{16,}$',
	},
	{
		attributeId: 'owner',
		attributeName: 'Owner',
		description:
			'Specify the LDAP/POSIX group or team responsible for managing this application',
		dataType: UIDataType.STRING,
		mandatory: true,
		helpText:
			'Enter the LDAP/POSIX group or team name that owns this application. This will be used for communication, notifications, and operational responsibilities.',
	},
];

export const CTI_FORM_ATTRIBUTES: UISnowAttribute[] = [
	{
		attributeId: 'cti.category',
		attributeName: 'CTI Category',
		description:
			'Select the primary CTI category required for ticket creation and operational classification within SNOW',
		dataType: UIDataType.STRING,
		mandatory: true,
		helpText:
			'Choose the high-level category that best represents your application type. This is required for creating support tickets and operational workflows in SNOW.',
	},
	{
		attributeId: 'cti.type',
		attributeName: 'CTI Type',
		description:
			'Specify the CTI type that provides more detailed classification within the selected category',
		dataType: UIDataType.STRING,
		mandatory: true,
		helpText:
			'Select the specific type that further defines your application within the chosen category. This helps with proper routing and handling of operational requests.',
	},
	{
		attributeId: 'cti.item',
		attributeName: 'CTI Item',
		description:
			"Enter the most specific CTI item that precisely identifies your application's operational characteristics",
		dataType: UIDataType.STRING,
		mandatory: true,
		helpText:
			"Provide the exact item classification that describes your application's primary operational function. This ensures accurate categorization for support and maintenance activities.",
	},
];

export const DEFAULT_APPLICATION_DATA: ApplicationProfileFormMetadata = {
	applicationName: '',
	description: '',
	status: Status.ACTIVE,
	domain: 'SNOW',
	subDomains: '',
	bindleId: '',
	owner: '',
	cti: {
		category: '',
		type: '',
		item: '',
	},
	createdBy: '',
	lastUpdatedBy: '',
};

export function getApplicationWizardSteps(): WizardStep<ApplicationProfileFormMetadata>[] {
	return [
		createWizardStep<ApplicationProfileFormMetadata>(
			'Application Details',
			[...APPLICATION_FORM_ATTRIBUTES, ...CTI_FORM_ATTRIBUTES],
			{
				reviewGroup: 'Application Details',
			}
		),
	];
}

export function convertToApplicationProfileData(
	formData: ApplicationProfileFormMetadata,
	userName?: string
): ApplicationProfileData {
	const subDomainsArray = parseCommaSeparatedString(formData.subDomains);

	return {
		applicationName: formData.applicationName,
		description: formData.description,
		status: formData.status,
		domain: formData.domain,
		subDomains: subDomainsArray,
		bindleId: formData.bindleId,
		owner: formData.owner,
		createdBy: userName,
		lastUpdatedBy: userName,
		cti: {
			category: formData.cti?.category,
			type: formData.cti?.type,
			item: formData.cti?.item,
		},
	};
}

export function convertFromApplicationProfileData(
	applicationData: ApplicationProfileData
): ApplicationProfileFormMetadata {
	const subDomainsString = applicationData.subDomains?.join(', ') || '';

	return {
		applicationName: applicationData.applicationName || '',
		description: applicationData.description || '',
		status: applicationData.status || Status.ACTIVE,
		domain: applicationData.domain || '',
		subDomains: subDomainsString,
		bindleId: applicationData.bindleId || '',
		owner: applicationData.owner || '',
		createdBy: applicationData.createdBy || '',
		lastUpdatedBy: applicationData.lastUpdatedBy || '',
		cti: {
			category: applicationData.cti?.category || '',
			type: applicationData.cti?.type || '',
			item: applicationData.cti?.item || '',
		},
	};
}
